import { useEffect, useState } from "react";
import { AlertTriangle, Info, Plug } from "lucide-react";
import ConnectERP from "./ConnectERP";
import ConnectEmedHub from "./ConnectEmedHub";
import ConnectBiometric from "./ConnectBiometric";
import useApiMethods from "../../lib/useApiMethods";
import { ERP_CONNECTION_STATUS, INTEGRATION_CONNECTION } from "../../constants/api";

const readAdmin = () =>
    JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

const Integrations = () => {
    const { fetch } = useApiMethods();
    const isAdmin = readAdmin();
    const [loading, setLoading] = useState(isAdmin);
    const [erpConnected, setErpConnected] = useState(false);
    const [emedhubLinked, setEmedhubLinked] = useState(false);

    useEffect(() => {
        if (!isAdmin) {
            setLoading(false);
            return;
        }

        const loadStatus = async () => {
            setLoading(true);
            try {
                const [erpRes, emedRes] = await Promise.all([
                    fetch(ERP_CONNECTION_STATUS),
                    fetch(INTEGRATION_CONNECTION),
                ]);
                setErpConnected(!!(erpRes?.status === "success" && erpRes.data?.is_active));
                setEmedhubLinked(!!(emedRes?.status === "success" && emedRes.data));
            } finally {
                setLoading(false);
            }
        };

        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const showSetupBanner = isAdmin && !loading && !erpConnected && !emedhubLinked;

    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <Plug className="h-5 w-5 text-sidebar-primary" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Integrations</h1>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
                    Connect HRZyra to your ERP platform, eMedHub (HIMS) and your biometric readers. Link each service below —
                    there is no separate User Permission entry; only organization admins can manage these connections.
                </p>
            </div>

            {loading && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 animate-pulse">
                    <div className="h-4 w-48 bg-gray-100 dark:bg-gray-700 rounded mb-2" />
                    <div className="h-3 w-full max-w-md bg-gray-100 dark:bg-gray-700 rounded" />
                </div>
            )}

            {showSetupBanner && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/20">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300" />
                    <div className="text-sm text-amber-900 dark:text-amber-100 space-y-1">
                        <p className="font-medium">No integrations linked yet</p>
                        <p className="text-amber-800/90 dark:text-amber-200/90">
                            ERP and eMedHub are not connected. Use the sections below to generate an ERP token
                            or save your eMedHub clinic details. Once eMedHub is saved, the{" "}
                            <strong>Integration</strong> screen in the left menu opens for doctor sync and mapping.
                        </p>
                    </div>
                </div>
            )}

            {!loading && isAdmin && (erpConnected || emedhubLinked) && (
                <div className="flex flex-wrap gap-2 text-xs">
                    <span className={`rounded-full px-2.5 py-1 border ${erpConnected ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800" : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"}`}>
                        ERP: {erpConnected ? "Connected" : "Not connected"}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 border ${emedhubLinked ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800" : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"}`}>
                        eMedHub: {emedhubLinked ? "Configured" : "Not configured"}
                    </span>
                </div>
            )}

            {!isAdmin && (
                <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
                    <Info className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Integration setup is restricted to organization administrators. Contact your org admin
                        if ERP or eMedHub needs to be linked.
                    </p>
                </div>
            )}

            <ConnectERP />
            <ConnectEmedHub />
            <ConnectBiometric />
        </div>
    );
};

export default Integrations;
